import React from 'react';
import { useTranslation } from 'react-i18next';

export interface ConsentSectionProps {
  marketingConsentCollection: boolean;
  marketingConsentPromotion: boolean;
  marketingConsentEmail: boolean;
  marketingConsentSMS: boolean;
  onChange: (field: string, value: boolean) => void;
}

/**
 * 수신동의 section — MatchGo "Bright" styling. Bordered consent card with a
 * collection row + promotion row, and email/SMS channel chips that activate
 * only when promotion consent is on.
 */
const ConsentSection: React.FC<ConsentSectionProps> = ({
  marketingConsentCollection,
  marketingConsentPromotion,
  marketingConsentEmail,
  marketingConsentSMS,
  onChange,
}) => {
  const { t } = useTranslation('profile');

  const handlePromotionChange = (checked: boolean) => {
    onChange('marketingConsentPromotion', checked);
    if (checked) {
      // Promotion consent requires at least one channel — default to email
      // when neither channel is currently selected.
      if (!marketingConsentEmail && !marketingConsentSMS) {
        onChange('marketingConsentEmail', true);
      }
    } else {
      onChange('marketingConsentEmail', false);
      onChange('marketingConsentSMS', false);
    }
  };

  // Toggling a channel. Unchecking the last remaining channel while promotion
  // is on would leave "consent with no channel", so we withdraw promotion too.
  const handleChannelChange = (
    field: 'marketingConsentEmail' | 'marketingConsentSMS',
    checked: boolean,
  ) => {
    onChange(field, checked);
    if (!checked) {
      const other =
        field === 'marketingConsentEmail' ? marketingConsentSMS : marketingConsentEmail;
      if (!other) {
        onChange('marketingConsentPromotion', false);
      }
    }
  };

  return (
    <section className="px-6 sm:px-8 py-7 border-t border-[#EEF1F6]">
      <div className="flex items-center justify-between gap-2 pb-3.5 mb-3 border-b-2 border-[#0F1E3D]">
        <h2 className="text-[18px] font-bold tracking-tight text-[#0F1E3D]">
          {t('myProfile.form.consent')}
        </h2>
        <span className="font-mono text-[10px] font-semibold text-[#6B7794] bg-[#F7F8FB] border border-[#E4E8F0] rounded px-2 py-0.5">
          {t('myProfile.form.savedTag', '저장 버튼 대상')}
        </span>
      </div>

      <p className="text-[13.5px] text-[#4A5D8C] mb-4">
        {t('myProfile.form.consentLead', '진행하는 이벤트와 회원님 맞춤 캠페인 추천을 받아보세요. 모두 선택 사항이며 언제든 변경할 수 있습니다.')}
      </p>

      <div className="border border-[#E4E8F0] rounded-xl overflow-hidden max-w-[560px]">
        {/* 개인정보 수집·이용동의 */}
        <label className="flex items-start gap-3 px-4 py-3.5 cursor-pointer hover:bg-[#F7F8FB] border-b border-[#EEF1F6]">
          <input
            id="marketingConsentCollection"
            type="checkbox"
            checked={marketingConsentCollection}
            onChange={(e) => onChange('marketingConsentCollection', e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#2D5BFF]"
          />
          <div className="flex-1 min-w-0 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[13.5px] text-[#0F1E3D] leading-snug">
              {t('myProfile.form.consentPersonalInfo')}
              <span className="ml-1.5 font-mono text-[9.5px] font-semibold text-[#6B7794] bg-white border border-[#E4E8F0] rounded px-1.5 py-0.5 align-middle">
                {t('myProfile.form.optional', '선택')}
              </span>
            </span>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] text-[#6B7794]">2025.12.10</span>
              <button
                type="button"
                className="text-xs text-[#2D5BFF] hover:underline"
                onClick={(e) => { e.preventDefault(); window.open('/legal/marketing-consent-collection', '_blank'); }}
              >
                {t('myProfile.form.consentViewDetails')}
              </button>
            </div>
          </div>
        </label>

        {/* 광고성 정보 수신 동의 (parent) */}
        <label className="flex items-start gap-3 px-4 py-3.5 cursor-pointer hover:bg-[#F7F8FB]">
          <input
            id="marketingConsentPromotion"
            type="checkbox"
            checked={marketingConsentPromotion}
            onChange={(e) => handlePromotionChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#2D5BFF]"
          />
          <div className="flex-1 min-w-0 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[13.5px] text-[#0F1E3D] leading-snug">
              {t('myProfile.form.consentMarketing')}
              <span className="ml-1.5 font-mono text-[9.5px] font-semibold text-[#6B7794] bg-white border border-[#E4E8F0] rounded px-1.5 py-0.5 align-middle">
                {t('myProfile.form.optional', '선택')}
              </span>
            </span>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] text-[#6B7794]">2025.12.10</span>
              <button
                type="button"
                className="text-xs text-[#2D5BFF] hover:underline"
                onClick={(e) => { e.preventDefault(); window.open('/legal/marketing-consent-promotion', '_blank'); }}
              >
                {t('myProfile.form.consentViewDetails')}
              </button>
            </div>
          </div>
        </label>

        {/* Email / SMS channel chips */}
        <div className={`flex items-center gap-2.5 flex-wrap px-4 py-3.5 bg-[#F7F8FB] border-t border-[#EEF1F6] transition-opacity ${marketingConsentPromotion ? '' : 'opacity-45 pointer-events-none'}`}>
          <span className="font-mono text-[11px] font-bold tracking-wide uppercase text-[#6B7794] mr-1">
            {t('myProfile.form.consentChannels', '수신 채널')}
          </span>
          <label
            className={`inline-flex items-center gap-2 text-[13px] px-3 py-2 rounded-lg border-[1.5px] cursor-pointer transition ${
              marketingConsentPromotion && marketingConsentEmail
                ? 'border-[#2D5BFF] bg-[#E6EDFF] text-[#2D5BFF] font-semibold'
                : 'border-[#E4E8F0] bg-white text-[#0F1E3D]'
            }`}
          >
            <input
              id="marketingConsentEmail"
              type="checkbox"
              checked={marketingConsentEmail}
              onChange={(e) => handleChannelChange('marketingConsentEmail', e.target.checked)}
              className="h-4 w-4 accent-[#2D5BFF]"
            />
            {t('myProfile.form.consentEmail')}
          </label>
          <label
            className={`inline-flex items-center gap-2 text-[13px] px-3 py-2 rounded-lg border-[1.5px] cursor-pointer transition ${
              marketingConsentPromotion && marketingConsentSMS
                ? 'border-[#2D5BFF] bg-[#E6EDFF] text-[#2D5BFF] font-semibold'
                : 'border-[#E4E8F0] bg-white text-[#0F1E3D]'
            }`}
          >
            <input
              id="marketingConsentSMS"
              type="checkbox"
              checked={marketingConsentSMS}
              onChange={(e) => handleChannelChange('marketingConsentSMS', e.target.checked)}
              className="h-4 w-4 accent-[#2D5BFF]"
            />
            {t('myProfile.form.consentSMS')}
          </label>
        </div>
      </div>

      <p className="text-[11.5px] text-[#6B7794] leading-relaxed mt-3 max-w-[560px]">
        {t('myProfile.form.consentNote', '수신 동의·철회 시 처리 결과를 등록하신 연락처로 통지하며, 광고성 정보는 야간(21~08시)에는 발송되지 않습니다. 거래·계약 등 의무적으로 안내되는 정보성 메시지는 동의 여부와 무관하게 발송될 수 있습니다.')}
      </p>
    </section>
  );
};

export default ConsentSection;
